import Info from '@material-ui/icons/Info'
import InfoOutlined from '@material-ui/icons/InfoOutlined'
import { logAmplitudeEvent } from 'amplitude/amplitude'
import { JIRA_SUPPORT_TICKET_LINK } from 'constants/links'
import { CurrencySymbolContext } from 'contexts/CurrencySymbolContext'
import { authSelectors } from 'ducks/auth'
import { Tooltip } from 'opensolar-ui'
import AccordionCard from 'projectSections/sections/info/AccordionCard'
import { useTranslate } from 'ra-core'
import { Button } from 'ra-ui-materialui'
import { FC, ReactElement } from 'react'
import { useFormState } from 'react-final-form'
import { useSelector } from 'react-redux'
import { NetworkTariffStaffAudit } from './NetworkTariffStaffAudit'
import { currencySymbolForCountry } from 'util/misc'
import { useStyles } from './styles'
import GeneralInfoFields from './tariffInputs/GeneralInfoFields'
import TaxDetailsFields from './tariffInputs/TaxDetailsFields'

type CustomToolTipTypes = {
  message: string | JSX.Element
  children: ReactElement<any, any> | null
  interactive?: boolean
  classNames?: string
}

export const InputWithToolTip: FC<CustomToolTipTypes> = ({ children, message, interactive, classNames }) => {
  const classes = useStyles()
  const translate = useTranslate()
  if (!message) {
    return children
  }
  return (
    <div className={classes.row}>
      {children}
      <Tooltip
        interactive={interactive}
        placement="right"
        title={typeof message === 'string' ? translate(message) : message}
        PopperProps={{
          style: {
            pointerEvents: 'auto',
          },
        }}
      >
        <InfoOutlined className={`${classes.infoIcon} ${classNames}`} />
      </Tooltip>
    </div>
  )
}

type PropTypes = {
  disabled?: boolean
  isEdit: boolean
  isCustom?: boolean
  /** Hide utility (retail) field — used for distributor-only network tariffs */
  isNetworkTariff?: boolean
  defaultCountry?: {
    url: string
    iso2: string
  }
}

const TariffInputs: FC<PropTypes> = ({ disabled, isEdit, isCustom, isNetworkTariff, defaultCountry }) => {
  const classes = useStyles()
  const translate = useTranslate()
  const formState = useFormState()
  const formValues = formState?.values
  const isStaff = useSelector(authSelectors.getIsStaff)
  const countryIso2 = formValues['country_iso2'] || defaultCountry?.iso2
  const currencySymbol = currencySymbolForCountry(countryIso2)
  const showNetworkStaffAudit = Boolean(isEdit && !isCustom && isNetworkTariff && isStaff)
  return (
    <CurrencySymbolContext.Provider value={currencySymbol}>
      {showNetworkStaffAudit && (
        <NetworkTariffStaffAudit
          createdDate={formValues.created_date}
          createdByEmail={formValues.created_by_role_email}
          modifiedDate={formValues.modified_date}
          modifiedByEmail={formValues.modified_by_role_email}
        />
      )}
      {isEdit && !isCustom && !isNetworkTariff && (
        <Button
          variant="contained"
          color="default"
          size="medium"
          label="Report Incorrect Tariff"
          startIcon={<Info />}
          onClick={() => {
            window.open(JIRA_SUPPORT_TICKET_LINK, '_blank')
            logAmplitudeEvent('report_incorrect_tariff_clicked', {
              id: formValues?.id,
              code: formValues?.code,
              utilityName: formValues?.utility_name,
            })
          }}
          className={classes.bottomMargin}
        />
      )}
      <div className={classes.accordionWrapper}>
        <AccordionCard
          defaultExpanded={true}
          disabled={disabled}
          name={'general-info'}
          title={translate('General Info')}
          content={GeneralInfoFields}
          titleIcon={() => <></>}
          contentProps={{ isCustom, isNetworkTariff, isEdit }}
        />
        <AccordionCard
          defaultExpanded={true}
          disabled={disabled}
          name={'tax-details'}
          title={translate('Charges, Credits & Tax Details')}
          content={TaxDetailsFields}
          titleIcon={() => <></>}
          contentProps={{ isCustom, countryIso2: countryIso2, isNetworkTariff }}
        />
      </div>
    </CurrencySymbolContext.Provider>
  )
}
export default TariffInputs
